import { call, put, takeLatest } from 'redux-saga/effects';
import { setToken, setPermissions, setUserInfo } from 'utils/crypto';
import { setSentryUserContextFromToken, clearSentryUserContext } from 'utils/sentry';
import { cancelPendingGridFilterSaves } from 'utils/hooks/savedFilters';
import { authService } from './auth.service';
import {
    LOG_IN,
    LOG_OUT,
    GET_RECOVERY_LINK,
    RESET_PASSWORD_REQUEST,
    CHANGE_PASSWORD_REQUEST,
    GET_MY_PROFILE,
    GET_ACCESS,
    GET_ACCESS_SUCCESS,
    ASSIGN_ACCESS,
    REMOVE_ACCESS,
    GET_MY_PROFILE_SUCCESS,
    CHANGE_NOTIFICATIONS,
    SAVE_QR_SCANNING,
    UPDATE_SCHEDULE_VIEW,
} from './auth.types';
import { httpRequestsOnErrorsActions } from '../http_requests_on_errors';
import { httpRequestsOnLoadActions } from '../http_requests_on_load';
import { httpRequestsOnSuccessActions } from '../http_requests_on_success';

function* logIn({ payload, type }) {
    yield put(httpRequestsOnErrorsActions.removeError(type));
    yield put(httpRequestsOnLoadActions.appendLoading(type));
    yield put(httpRequestsOnSuccessActions.removeSuccess(type));
    try {
        const res = yield call(authService.login, payload?.user);
        yield put(httpRequestsOnLoadActions.removeLoading(type));

        if (res.data?.permissions) {
            setPermissions(res.data?.permissions || []);
        }
        // localStorage.setItem('baseUrl', process.env.REACT_APP_API_BASE);
        setToken(res?.data?.token);
        setSentryUserContextFromToken(res?.data?.token);
        window.location.replace('/dashboard');
        yield put(httpRequestsOnLoadActions.removeLoading(type));
    } catch (err) {
        const error = err?.data?.message ? err?.data?.message : err?.response?.data?.message;
        yield put(httpRequestsOnErrorsActions.appendError(type, error));
        yield put(httpRequestsOnLoadActions.removeLoading(type));
    }
}

function* logOut(action) {
    yield put(httpRequestsOnLoadActions.appendLoading(action.type));
    yield put(httpRequestsOnErrorsActions.removeError(action.type));
    yield put(httpRequestsOnSuccessActions.removeSuccess(action.type));
    try {
        yield call(authService.logOut);
        yield put(httpRequestsOnLoadActions.removeLoading(action.type));
        yield put(httpRequestsOnSuccessActions.appendSuccess(action.type));
    } catch (err) {
        yield put(httpRequestsOnLoadActions.removeLoading(action.type));
        yield put(httpRequestsOnErrorsActions.appendError(action.type, err?.data?.message));
    } finally {
        clearSentryUserContext();
        // A debounced filter save still in flight would fire after the token is gone,
        // against the next user's session. The store slice resets on LOG_OUT itself.
        cancelPendingGridFilterSaves();
        // Preserve keys that should survive logout
        const lastSeenVersionId = localStorage.getItem('last_seen_version_id');
        // remove everything from local storage and cache of the browser
        localStorage.clear();
        // Restore non-auth keys
        if (lastSeenVersionId) {
            localStorage.setItem('last_seen_version_id', lastSeenVersionId);
        }
        // Clear session storage
        sessionStorage.clear();
        if ('serviceWorker' in navigator) {
            navigator.serviceWorker.getRegistrations().then(function (registrations) {
                for (let registration of registrations) {
                    registration.unregister();
                }
            });
        }
        // window.location.replace('/');
    }
}

function* getLink({ payload, type }) {
    yield put(httpRequestsOnErrorsActions.removeError(type));
    yield put(httpRequestsOnLoadActions.appendLoading(type));
    yield put(httpRequestsOnSuccessActions.removeSuccess(type));
    try {
        yield call(authService.getLink, payload?.email, process.env.REACT_APP_API_BASE);
        yield put(httpRequestsOnLoadActions.removeLoading(type));
        yield put(httpRequestsOnSuccessActions.appendSuccess(type));
    } catch (err) {
        yield put(httpRequestsOnErrorsActions.appendError(type, err?.response?.data?.message || err?.data?.message));
        yield put(httpRequestsOnLoadActions.removeLoading(type));
    }
}

function* resetPassword(action) {
    yield put(httpRequestsOnErrorsActions.removeError(action.type));
    yield put(httpRequestsOnLoadActions.appendLoading(action.type));
    try {
        // const base = process.env.REACT_APP_API_BASE;
        if (action?.payload?.passwords?.type === 'reset') {
            const res = yield call(authService.resetPass, action?.payload?.passwords);
            // localStorage.setItem('baseUrl', base);

            yield put(httpRequestsOnLoadActions.removeLoading(action.type));
            yield put(httpRequestsOnSuccessActions.appendSuccess(action.type));
            setToken(res.data.token);

            if (res.data?.permissions) {
                setPermissions(res.data?.permissions || []);
            }
            window.location.replace('/dashboard');
        } else {
            const res = yield call(authService.confirmAccount, action?.payload?.passwords);
            // localStorage.setItem('baseUrl', base);

            yield put(httpRequestsOnLoadActions.removeLoading(action.type));
            setToken(res.data.token);
            if (res.data?.permissions) {
                setPermissions(res.data?.permissions || []);
            }
            window.location.replace('/dashboard');
        }
    } catch (err) {
        const errMessage = err?.response?.data?.message ? err?.response?.data?.message : err?.data?.message;
        yield put(httpRequestsOnErrorsActions.appendError(action.type, errMessage));
        yield put(httpRequestsOnLoadActions.removeLoading(action.type));
    }
}

function* changePassword(action) {
    yield put(httpRequestsOnErrorsActions.removeError(action.type));
    yield put(httpRequestsOnLoadActions.appendLoading(action.type));
    yield put(httpRequestsOnSuccessActions.removeSuccess(action.type));
    try {
        const res = yield call(authService.changePasswordService, action.payload.data);
        yield put(httpRequestsOnLoadActions.removeLoading(action.type));
        yield put(httpRequestsOnSuccessActions.appendSuccess(action.type));
        setToken(res?.data?.accessToken);
    } catch (err) {
        yield put(httpRequestsOnLoadActions.removeLoading(action.type));
        yield put(httpRequestsOnErrorsActions.appendError(action.type, err?.data?.message));
    }
}
function* changeNotifications(action) {
    yield put(httpRequestsOnErrorsActions.removeError(action.type));
    yield put(httpRequestsOnLoadActions.appendLoading(action.type));
    yield put(httpRequestsOnSuccessActions.removeSuccess(action.type));
    try {
        yield call(authService.changeNotificationService, action?.payload?.id, action?.payload?.data);
        yield put({
            type: GET_MY_PROFILE,
        });
        yield put(httpRequestsOnLoadActions.removeLoading(action.type));
        yield put(httpRequestsOnSuccessActions.appendSuccess(action.type));
    } catch (err) {
        yield put(httpRequestsOnLoadActions.removeLoading(action.type));
        yield put(httpRequestsOnErrorsActions.appendError(action.type, err?.data?.message));
    }
}

function* getMuProfile(action) {
    if (action?.payload?.load !== 'noLoad') {
        yield put(httpRequestsOnLoadActions.appendLoading(action.type));
    }
    yield put(httpRequestsOnErrorsActions.removeError(action.type));
    try {
        const res = yield call(authService.myProfileService);
        yield put(httpRequestsOnLoadActions.removeLoading(action.type));
        yield put({
            type: GET_MY_PROFILE_SUCCESS,
            payload: res?.data,
        });
        if (res.data) {
            setUserInfo(res.data);
        }
    } catch (err) {
        yield put(httpRequestsOnLoadActions.removeLoading(action.type));
        yield put(httpRequestsOnErrorsActions.appendError(action.type, err?.data?.message));
    }
}

/** Access service */
function* getAccessService(action) {
    if (action?.payload?.load !== 'noLoad') {
        yield put(httpRequestsOnLoadActions.appendLoading(action.type));
    }
    yield put(httpRequestsOnErrorsActions.removeError(action.type));
    try {
        const res = yield call(authService.getAccessService, action.payload.userId);
        yield put(httpRequestsOnLoadActions.removeLoading(action.type));
        yield put({
            type: GET_ACCESS_SUCCESS,
            payload: res.data,
        });
    } catch (err) {
        yield put(httpRequestsOnLoadActions.removeLoading(action.type));
        if (err?.data?.message === 'Internal server error') {
            yield put(httpRequestsOnErrorsActions.appendError(action.type, err?.data?.message));
        }
    }
}

function* assignAccess(action) {
    yield put(httpRequestsOnLoadActions.appendLoading(action.type));
    yield put(httpRequestsOnErrorsActions.removeError(action.type));
    yield put(httpRequestsOnSuccessActions.removeSuccess(action.type));
    try {
        yield call(authService.addAccessService, action?.payload?.userId, action?.payload?.roleId);
        yield put(httpRequestsOnLoadActions.removeLoading(action.type));
        yield put(httpRequestsOnSuccessActions.appendSuccess(action.type));
        yield put({
            type: GET_ACCESS,
            payload: { userId: action.payload.userId, load: 'noLoad' },
        });
    } catch (err) {
        yield put(httpRequestsOnLoadActions.removeLoading(action.type));
        yield put(httpRequestsOnErrorsActions.appendError(action.type, err?.data?.message));
    }
}

function* removeAccess(action) {
    yield put(httpRequestsOnLoadActions.appendLoading(action.type));
    yield put(httpRequestsOnErrorsActions.removeError(action.type));
    yield put(httpRequestsOnSuccessActions.removeSuccess(action.type));
    try {
        yield call(authService.removeAccessService, action?.payload?.userId, action?.payload?.roleId);
        yield put(httpRequestsOnLoadActions.removeLoading(action.type));
        yield put(httpRequestsOnSuccessActions.appendSuccess(action.type));
        yield put({
            type: GET_ACCESS,
            payload: { userId: action?.payload?.userId, load: 'noLoad' },
        });
    } catch (err) {
        yield put(httpRequestsOnLoadActions.removeLoading(action.type));
        yield put(httpRequestsOnErrorsActions.appendError(action.type, err?.data?.message));
    }
}

/** End */

function* saveQrScanning(action) {
    try {
        yield call(authService.saveQrScanningService, action?.payload?.body);
    } catch (err) {}
}

function* updateScheduleView(action) {
    yield put(httpRequestsOnErrorsActions.removeError(action.type));
    yield put(httpRequestsOnLoadActions.appendLoading(action.type));
    yield put(httpRequestsOnSuccessActions.removeSuccess(action.type));
    try {
        yield call(authService.updateScheduleViewService, action?.payload?.data);
        const res = yield call(authService.myProfileService);
        yield put({
            type: GET_MY_PROFILE_SUCCESS,
            payload: res?.data,
        });
        yield put(httpRequestsOnLoadActions.removeLoading(action.type));
        yield put(httpRequestsOnSuccessActions.appendSuccess(action.type));
    } catch (err) {
        yield put(httpRequestsOnLoadActions.removeLoading(action.type));
        yield put(httpRequestsOnErrorsActions.appendError(action.type, err?.data?.message));
    }
}

export const watchAuth = function* watchUserAuth() {
    yield takeLatest(LOG_IN, logIn);
    yield takeLatest(LOG_OUT, logOut);
    yield takeLatest(GET_MY_PROFILE, getMuProfile);
    yield takeLatest(GET_RECOVERY_LINK, getLink);
    yield takeLatest(RESET_PASSWORD_REQUEST, resetPassword);
    yield takeLatest(CHANGE_PASSWORD_REQUEST, changePassword);
    yield takeLatest(CHANGE_NOTIFICATIONS, changeNotifications);

    /** Access service */
    yield takeLatest(GET_ACCESS, getAccessService);
    yield takeLatest(ASSIGN_ACCESS, assignAccess);
    yield takeLatest(REMOVE_ACCESS, removeAccess);
    /** End */

    yield takeLatest(SAVE_QR_SCANNING, saveQrScanning);
    yield takeLatest(UPDATE_SCHEDULE_VIEW, updateScheduleView);
};
